import PropTypes from 'prop-types';

import Button from 'components/ui-components/Button';
import { StoreIcon } from 'assets/icons';
import { useBasketAction } from 'hooks/useBasketAction';
import useProductShowStore from 'stores/product_show_store/useProductShowStore';
import { linkToCollection } from 'lib/addToBasketHelpers';
import usePricingStore from 'stores/pricing_store/usePricingStore';
import { grey50, ctaGreen, white } from 'constants/variables';

import CollectionStock from 'components/product_show/reserve_and_collect/collection_stock';
import { branchTermSingularCapitalised } from 'utils/terminology';

const ReserveAndCollect = ({
  isStoreSelected,
  isDeliveryOnly,
  stepId,
  storeStockUrl,
  addToBasketUrl,
  title,
  category,
  stockCode,
  partCode,
  brand,
  isSimilarStock,
  disabled,
  type,
  showStock,
}) => {
  const {
    quantitySelectorInput,
    productBasketQuantity,
    isAddingToBasket,
    selectedStore,
    storeStock,
  } = useProductShowStore();

  const { productPrice } = usePricingStore();
  const { forceAction } = useBasketAction({
    stepId,
    addToBasketUrl,
    productPrice,
    isReserveCollect: true,
    title,
    brand: brand || '',
    category,
    partCode,
    stockCode,
    type,
    isSimilarStock,
    stockLevel: storeStock,
    onBackOrderRequired: null,
  });

  const hasSufficientStock =
    quantitySelectorInput > 0 && quantitySelectorInput <= storeStock;

  const isButtonAddingToBasket = isAddingToBasket === 'collection';
  const isButtonDisabled =
    isButtonAddingToBasket ||
    (isStoreSelected && selectedStore && storeStock === '0') ||
    !productPrice ||
    (isStoreSelected && selectedStore && !hasSufficientStock) ||
    isDeliveryOnly;

  const buttonText = () => {
    switch (true) {
      case disabled:
        return 'Collection Disabled';
      case isDeliveryOnly:
        return 'Delivery Only';
      case isAddingToBasket === 'collection':
        return 'Added to Basket';
      default:
        return 'Reserve & Collect';
    }
  };

  const iconColor = isButtonAddingToBasket
    ? ctaGreen
    : isButtonDisabled
    ? grey50
    : white;

  const baseClasses =
    'js-product-action-btn product-action-btn cef-btn ff-body fw-600 fl fl-row ai-c fs-14 l-fs-16 pt-14 pb-14';
  const colorClass = isStoreSelected ? 'cef-btn--green' : 'cef-btn--grey';
  const addingToBasketClass = isButtonAddingToBasket
    ? 'js-product-action-btn--disabled'
    : '';

  const buttonClass = `${baseClasses} ${colorClass} ${addingToBasketClass}`;
  const shouldShowReserveAndCollectButton =
    (isStoreSelected && selectedStore) || disabled || isDeliveryOnly;

  return (
    <div className="cef__reserve-and-collect fl fl-col fl-grow-1">
      {shouldShowReserveAndCollectButton ? (
        <Button
          role="button"
          name="Reserve and Collect"
          className={buttonClass}
          handleClick={forceAction}
          disabled={isButtonDisabled}>
          <StoreIcon width={24} height={24} fillColor={iconColor} />
          {buttonText()}
        </Button>
      ) : (
        <a
          data-gtm-click="true"
          href={linkToCollection(
            stepId,
            productBasketQuantity || quantitySelectorInput,
          )}
          className="product-show__collection-btn cef-btn cef-btn--grey product-action-btn ta-c">
          <StoreIcon width={24} height={24} fillColor={white} />
          <span>
            Select a <span className="d-none xl-d-inline">Collection </span>
            {branchTermSingularCapitalised}
          </span>
        </a>
      )}
      {showStock &&
        !isDeliveryOnly &&
        !disabled &&
        isStoreSelected &&
        selectedStore && (
          <CollectionStock
            stepId={stepId}
            isDeliveryOnly={isDeliveryOnly}
            storeStockUrl={storeStockUrl}
            storeStock={storeStock}
            selectedStore={selectedStore}
            hasSufficientStock={hasSufficientStock}
            disabled={disabled}
          />
        )}
    </div>
  );
};

ReserveAndCollect.propTypes = {
  title: PropTypes.string,
  brand: PropTypes.string,
  isSimilarStock: PropTypes.bool,
  category: PropTypes.string,
  partCode: PropTypes.string,
  isDeliveryOnly: PropTypes.bool,
  isStoreSelected: PropTypes.bool,
  storeStockUrl: PropTypes.string,
  addToBasketUrl: PropTypes.string,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  stepId: PropTypes.string,
  stockCode: PropTypes.string,
  showStock: PropTypes.bool,
};

export default ReserveAndCollect;
